import React, { useState } from 'react'
import {X, LogIn, UserPlus, KeyRound} from 'lucide-react'
import { lumi } from '@/lib/lumi'

interface AuthModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

export default function AuthModal({ isOpen, onClose, onSuccess }: AuthModalProps) {
  const [mode, setMode] = useState<'login' | 'register' | 'reset'>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [userName, setUserName] = useState('')
  const [otp, setOtp] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [otpSent, setOtpSent] = useState(false)
  const [cooldown, setCooldown] = useState(0)

  if (!isOpen) return null

  const startCooldown = () => {
    setCooldown(60)
    const interval = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(interval)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  const handleSendOtp = async () => {
    setError(null)
    setLoading(true)
    try {
      await lumi.auth.sendOtp({
        email,
        type: mode === 'register' ? 'register' : 'resetPassword'
      })
      setOtpSent(true)
      startCooldown()
      console.log('OTP sent to:', email)
    } catch (err: any) {
      setError(err?.message || 'Failed to send OTP')
      console.error('OTP error:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const { user } = await lumi.auth.signInWithPassword({ email, password })
      console.log('Login successful:', user)
      onSuccess()
      onClose()
    } catch (err: any) {
      setError(err?.message || 'Login failed. Please check your credentials.')
      console.error('Login error:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!otpSent) {
      setError('Please send OTP first')
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters')
      return
    }

    setLoading(true)
    try {
      const { user } = await lumi.auth.signUpWithPassword({
        email,
        password,
        otp,
        userName: userName || undefined
      })
      console.log('Registration successful:', user)
      onSuccess()
      onClose()
    } catch (err: any) {
      setError(err?.message || 'Registration failed. Please check your information.')
      console.error('Registration error:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!otpSent) {
      setError('Please send OTP first')
      return
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters')
      return
    }

    setLoading(true)
    try {
      await lumi.auth.resetPassword({
        email,
        newPassword: password,
        otp
      })
      console.log('Password reset successful')
      setMode('login')
      setError(null)
      setOtpSent(false)
      setPassword('')
      setOtp('')
    } catch (err: any) {
      setError(err?.message || 'Password reset failed.')
      console.error('Password reset error:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    if (mode === 'login') return handleLogin(e)
    if (mode === 'register') return handleRegister(e)
    if (mode === 'reset') return handleResetPassword(e)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-8 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close modal"
        >
          <X size={24} />
        </button>

        <div className="flex items-center justify-center mb-6">
          <div className="bg-blue-800 p-3 rounded-full">
            {mode === 'login' && <LogIn className="text-white" size={28} />}
            {mode === 'register' && <UserPlus className="text-white" size={28} />}
            {mode === 'reset' && <KeyRound className="text-white" size={28} />}
          </div>
        </div>

        <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">
          {mode === 'login' && 'Welcome Back'}
          {mode === 'register' && 'Create Account'}
          {mode === 'reset' && 'Reset Password'}
        </h2>
        <p className="text-gray-600 text-center mb-6 text-sm">
          {mode === 'login' && 'Sign in to save and manage your roof estimates'}
          {mode === 'register' && 'Save unlimited estimates and access them anytime'}
          {mode === 'reset' && 'Enter your email to receive a reset code'}
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-300 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email */}
          <div>
            <label htmlFor="auth-email" className="block text-sm font-semibold text-gray-700 mb-2">
              Email Address
            </label>
            <input
              id="auth-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-800 focus:outline-none"
              required
            />
          </div>

          {/* OTP Section for Register/Reset */}
          {(mode === 'register' || mode === 'reset') && (
            <div>
              <label htmlFor="auth-otp" className="block text-sm font-semibold text-gray-700 mb-2">
                Verification Code
              </label>
              <div className="flex gap-2">
                <input
                  id="auth-otp"
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="Enter OTP"
                  className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-800 focus:outline-none"
                  disabled={!otpSent}
                  required
                />
                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={!email || loading || cooldown > 0}
                  className="px-4 py-3 bg-blue-800 hover:bg-blue-900 disabled:bg-gray-400 text-white font-semibold rounded-lg transition-colors whitespace-nowrap"
                >
                  {cooldown > 0 ? `${cooldown}s` : otpSent ? 'Resend' : 'Send OTP'}
                </button>
              </div>
              {otpSent && (
                <p className="text-xs text-green-600 mt-1">Code sent! Check your email.</p>
              )}
            </div>
          )}

          {/* Username for Register */}
          {mode === 'register' && (
            <div>
              <label htmlFor="auth-username" className="block text-sm font-semibold text-gray-700 mb-2">
                Name (Optional)
              </label>
              <input
                id="auth-username"
                type="text"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                placeholder="Your name"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-800 focus:outline-none"
              />
            </div>
          )}

          {/* Password */}
          <div>
            <label htmlFor="auth-password" className="block text-sm font-semibold text-gray-700 mb-2">
              Password
            </label>
            <input
              id="auth-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={mode === 'reset' ? 'New password' : 'Enter password'}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-800 focus:outline-none"
              required
              minLength={6}
            />
          </div>

          {/* Confirm Password for Register */}
          {mode === 'register' && (
            <div>
              <label htmlFor="auth-confirm-password" className="block text-sm font-semibold text-gray-700 mb-2">
                Confirm Password
              </label>
              <input
                id="auth-confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm password"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-800 focus:outline-none"
                required
              />
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || (mode !== 'login' && !otpSent)}
            className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-bold py-3 px-6 rounded-lg transition-colors shadow-lg"
          >
            {loading ? 'Processing...' : mode === 'login' ? 'Sign In' : mode === 'register' ? 'Create Account' : 'Reset Password'}
          </button>
        </form>

        {/* Mode Switcher */}
        <div className="mt-6 text-center text-sm space-y-2">
          {mode === 'login' && (
            <>
              <p className="text-gray-600">
                Don't have an account?{' '}
                <button onClick={() => { setMode('register'); setError(null); setOtpSent(false) }} className="text-blue-800 font-semibold hover:underline">
                  Create one
                </button>
              </p>
              <p className="text-gray-600">
                <button onClick={() => { setMode('reset'); setError(null); setOtpSent(false) }} className="text-blue-800 font-semibold hover:underline">
                  Forgot password?
                </button>
              </p>
            </>
          )}
          {mode === 'register' && (
            <p className="text-gray-600">
              Already have an account?{' '}
              <button onClick={() => { setMode('login'); setError(null); setOtpSent(false) }} className="text-blue-800 font-semibold hover:underline">
                Sign in
              </button>
            </p>
          )}
          {mode === 'reset' && (
            <p className="text-gray-600">
              Remember your password?{' '}
              <button onClick={() => { setMode('login'); setError(null); setOtpSent(false) }} className="text-blue-800 font-semibold hover:underline">
                Sign in
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
